package top.wcpe.allincore.dsl.compiler.parse

import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.NamedArg
import top.wcpe.allincore.dsl.compiler.ast.StmtAst
import top.wcpe.allincore.dsl.compiler.diag.DslError
import top.wcpe.allincore.dsl.compiler.lex.Token
import top.wcpe.allincore.dsl.compiler.lex.TokenType
import top.wcpe.allincore.dsl.compiler.source.SourcePosition

/**
 * 表达式解析器（C1 完整版）。
 *
 * 优先级（由低到高，与 Kotlin/Java 习惯对齐）：
 *
 * ```
 * ternary     : cond ? a : b              （右结合）
 * elvis       : a ?: b                     （右结合）
 * logicalOr   : a || b
 * logicalAnd  : a && b
 * equality    : a == b | a != b
 * comparison  : < <= > >=
 * additive    : + -
 * multiplicative : * / %
 * unary       : -x | !x
 * postfix     : primary (.m | ?.m | (args))*
 * primary     : literal | state.x | theme.x | ident | if (...) a else b | (...) | null | "${...}"
 * ```
 *
 * 字符串插值 token 流（与 [top.wcpe.allincore.dsl.compiler.lex.Lexer] 约定一致）：
 *
 * ```
 * INTERPOLATED_STRING_START
 * STRING_PART
 * (INTERPOLATION_START expr INTERPOLATION_END STRING_PART)*
 * ```
 */
internal class ExpressionParser(
    private val p: Parser,
    /**
     * 是否允许把裸标识符（未跟 `(` 的 IDENTIFIER）解析为 [ExprAst.IdentRef]。
     *
     * 仅 request 回调体打开此开关，用于把 `payload`/`error` 解析为参数引用。
     * 局部 `var` 的引用通过 Parser 的局部变量栈识别为 [ExprAst.LocalRef]，
     * 与本开关解耦。
     */
    private val allowBareIdent: Boolean = false,
    /**
     * `when` 表达式分支体解析器（C2）。
     *
     * 仅在 ActionBlockParser 创建 ExpressionParser 时注入；当表达式位置出现
     * `when (...)` 时调用以解析整个 when 节点。null 表示当前上下文不支持 when
     * 表达式（如 element prop 表达式）。
     */
    private val whenParser: (() -> ExprAst.WhenExpr)? = null,
    /**
     * lambda 体解析器（C3）。
     *
     * 由 ActionBlockParser 注入：负责把已声明参数 push 进局部作用域，
     * 解析 `{` `->` 之后到 `}` 之前的语句序列（不消费 `}`）。null 表示
     * 当前上下文（如 prop 表达式）不支持 lambda 体。
     */
    private val lambdaBodyParser: ((List<String>) -> List<top.wcpe.allincore.dsl.compiler.ast.StmtAst>)? = null,
) {

    fun parse(): ExprAst = parseTernary()

    /**
     * 公开入口：将当前位置紧邻的 `{ params -> body }` 解析为独立的 [ExprAst.LambdaExpr]。
     *
     * 用于 screen 顶层 `onKey { event -> ... }` 等"非表达式上下文中的 lambda"场景。
     * 若当前 token 不是 `{` 或上下文未注册 lambdaBodyParser，则报错。
     */
    fun parseStandaloneLambda(): ExprAst.LambdaExpr = parseLambda()

    // ---------- 三元 ----------
    private fun parseTernary(): ExprAst {
        val cond = parseElvis()
        if (!p.check(TokenType.QUESTION)) return cond
        p.advance()
        val thenExpr = parseTernary()
        p.expect(TokenType.COLON, "三元表达式缺少 ':'")
        val elseExpr = parseTernary()
        return ExprAst.Ternary(cond, thenExpr, elseExpr, cond.position.endingAt(elseExpr.range.end))
    }

    // ---------- Elvis（右结合） ----------
    private fun parseElvis(): ExprAst {
        val left = parseLogicalOr()
        if (!p.check(TokenType.ELVIS)) return left
        p.advance()
        val right = parseElvis()
        return ExprAst.Elvis(left, right, left.position.endingAt(right.range.end))
    }

    // ---------- 逻辑 ----------
    private fun parseLogicalOr(): ExprAst {
        var left = parseLogicalAnd()
        while (p.check(TokenType.OR_OR)) {
            val tok = p.advance()
            val right = parseLogicalAnd()
            left = ExprAst.BinaryOp("||", left, right, tok.position)
        }
        return left
    }

    private fun parseLogicalAnd(): ExprAst {
        var left = parseEquality()
        while (p.check(TokenType.AND_AND)) {
            val tok = p.advance()
            val right = parseEquality()
            left = ExprAst.BinaryOp("&&", left, right, tok.position)
        }
        return left
    }

    // ---------- 相等 / 比较 ----------
    private fun parseEquality(): ExprAst {
        var left = parseComparison()
        while (p.check(TokenType.EQ_EQ) || p.check(TokenType.NOT_EQ)) {
            val tok = p.advance()
            val op = if (tok.type == TokenType.EQ_EQ) "==" else "!="
            val right = parseComparison()
            left = ExprAst.BinaryOp(op, left, right, tok.position)
        }
        return left
    }

    private fun parseComparison(): ExprAst {
        var left = parseRange()
        while (true) {
            // M5-c：`expr is TypeName` 类型判断；与比较运算符同级，左结合。
            if (p.check(TokenType.KW_IS)) {
                val tok = p.advance()
                val nameTok = parseIsTypeName()
                left = ExprAst.IsExpr(left, nameTok, p.completePosition(tok.position))
                continue
            }
            if (!(p.check(TokenType.LT) || p.check(TokenType.LT_EQ)
                    || p.check(TokenType.GT) || p.check(TokenType.GT_EQ))
            ) break
            val tok = p.advance()
            val op = when (tok.type) {
                TokenType.LT -> "<"
                TokenType.LT_EQ -> "<="
                TokenType.GT -> ">"
                TokenType.GT_EQ -> ">="
                else -> error("unreachable")
            }
            val right = parseRange()
            left = ExprAst.BinaryOp(op, left, right, tok.position)
        }
        return left
    }

    /**
     * 解析 `is` 之后的类型名（M5-c 第一版仅支持单个 IDENTIFIER 或基础类型关键字）。
     */
    private fun parseIsTypeName(): String {
        val cur = p.current()
        return when (cur.type) {
            TokenType.IDENTIFIER -> { p.advance(); cur.lexeme }
            TokenType.KW_STRING -> { p.advance(); "String" }
            TokenType.KW_NUMBER -> { p.advance(); "Number" }
            TokenType.KW_BOOL -> { p.advance(); "Boolean" }
            TokenType.KW_COLOR -> { p.advance(); "Color" }
            else -> p.fail("'is' 后缺少类型名", cur.position)
        }
    }

    // ---------- range（C2，左结合，介于 comparison 与 additive 之间） ----------
    private fun parseRange(): ExprAst {
        var left = parseAdditive()
        while (p.check(TokenType.RANGE) || p.check(TokenType.KW_UNTIL)) {
            val tok = p.advance()
            val kind = if (tok.type == TokenType.RANGE) {
                ExprAst.RangeKind.CLOSED
            } else {
                ExprAst.RangeKind.HALF_OPEN
            }
            val right = parseAdditive()
            left = ExprAst.RangeExpr(left, right, kind, left.position.endingAt(right.range.end))
        }
        return left
    }

    // ---------- 算术 ----------
    private fun parseAdditive(): ExprAst {
        var left = parseMultiplicative()
        while (p.check(TokenType.PLUS) || p.check(TokenType.MINUS)) {
            val tok = p.advance()
            val op = if (tok.type == TokenType.PLUS) "+" else "-"
            val right = parseMultiplicative()
            left = ExprAst.BinaryOp(op, left, right, tok.position)
        }
        return left
    }

    private fun parseMultiplicative(): ExprAst {
        var left = parseUnary()
        while (p.check(TokenType.STAR) || p.check(TokenType.SLASH) || p.check(TokenType.PERCENT)) {
            val tok = p.advance()
            val op = when (tok.type) {
                TokenType.STAR -> "*"
                TokenType.SLASH -> "/"
                TokenType.PERCENT -> "%"
                else -> error("unreachable")
            }
            val right = parseUnary()
            left = ExprAst.BinaryOp(op, left, right, tok.position)
        }
        return left
    }

    // ---------- 一元 ----------
    private fun parseUnary(): ExprAst {
        if (p.check(TokenType.BANG)) {
            val tok = p.advance()
            val operand = parseUnary()
            return ExprAst.UnaryOp("!", operand, tok.position.endingAt(operand.range.end))
        }
        if (p.check(TokenType.MINUS)) {
            // 数字字面量紧跟 `-` 时折叠为常量 NumberLit，保留与旧实现一致的行为，
            // 同时避免负值 Double 在求值阶段额外一次开销。
            val nextType = p.peek(1).type
            if (nextType == TokenType.NUMBER) {
                val minus = p.advance()
                val numTok = p.advance()
                val v = (numTok.literalValue as Number).toDouble()
                return ExprAst.Literal.NumberLit(-v, minus.position.endingAt(numTok.range.end))
            }
            val tok = p.advance()
            val operand = parseUnary()
            return ExprAst.UnaryOp("-", operand, tok.position.endingAt(operand.range.end))
        }
        return parsePostfix()
    }

    // ---------- 后缀（成员访问 / 安全成员 / 方法调用 / 索引 / 通用调用 / trailing lambda） ----------
    private fun parsePostfix(): ExprAst {
        var expr = parsePrimary()
        while (true) {
            when {
                p.check(TokenType.DOT) -> {
                    val dotTok = p.advance()
                    val nameTok = expectMemberName("'.' 后缺少成员名")
                    expr = if (p.check(TokenType.LPAREN)) {
                        val args = parseCallArgs()
                        val tl = tryParseTrailingLambda()
                        val finalArgs = if (tl != null) args + tl else args
                        ExprAst.MemberCall(expr, nameTok.lexeme, finalArgs, p.completePosition(dotTok.position))
                    } else {
                        ExprAst.MemberAccess(expr, nameTok.lexeme, p.completePosition(dotTok.position, nameTok))
                    }
                }
                p.check(TokenType.SAFE_CALL) -> {
                    val safeTok = p.advance()
                    val nameTok = expectMemberName("'?.' 后缺少成员名")
                    expr = if (p.check(TokenType.LPAREN)) {
                        val args = parseCallArgs()
                        val tl = tryParseTrailingLambda()
                        val finalArgs = if (tl != null) args + tl else args
                        ExprAst.SafeMemberCall(expr, nameTok.lexeme, finalArgs, p.completePosition(safeTok.position))
                    } else {
                        ExprAst.SafeMemberAccess(expr, nameTok.lexeme, p.completePosition(safeTok.position, nameTok))
                    }
                }
                // M10 Stage 6：`target!!` 非空断言后缀；左结合，链式 `a!!!!` 由循环自然处理
                p.check(TokenType.BANG_BANG) -> {
                    val tok = p.advance()
                    expr = ExprAst.NonNullAssert(expr, tok.position)
                }
                p.check(TokenType.LBRACKET) -> {
                    val lb = p.advance()
                    val idx = parse()
                    p.expect(TokenType.RBRACKET, "索引访问缺少 ']'")
                    expr = ExprAst.IndexExpr(expr, idx, p.completePosition(lb.position))
                }
                p.check(TokenType.LPAREN) -> {
                    // M5-b：通用调用支持命名实参
                    val pos = p.current().position
                    val (posArgs, namedArgs) = parseCallArgsWithNamed()
                    val tl = tryParseTrailingLambda()
                    val finalArgs = if (tl != null) posArgs + tl else posArgs
                    expr = ExprAst.CallExpr(expr, finalArgs, p.completePosition(pos), namedArgs)
                }
                isTrailingLambdaStart(expr) -> {
                    // `f { ... }` 无显式 `()` 形态：把 lambda 作为唯一参数包成 CallExpr
                    val lam = parseLambda()
                    expr = ExprAst.CallExpr(expr, listOf(lam), expr.position.endingAt(lam.range.end))
                }
                else -> return expr
            }
        }
    }

    /**
     * 尝试解析紧邻的 trailing lambda：仅当当前 token 为 `{` 且当前上下文支持
     * lambda 时消费；否则返回 null。
     */
    private fun tryParseTrailingLambda(): ExprAst.LambdaExpr? {
        if (lambdaBodyParser == null) return null
        if (!p.check(TokenType.LBRACE)) return null
        return parseLambda()
    }

    /** 已解析为 callee 形态的表达式后，若紧邻 `{` 则视为 trailing lambda。 */
    private fun isTrailingLambdaStart(callee: ExprAst): Boolean {
        if (lambdaBodyParser == null) return false
        if (!p.check(TokenType.LBRACE)) return false
        // 只允许已知形如 callee 的表达式触发：identifier / 局部变量 / 成员访问 / fun ref
        return callee is ExprAst.IdentRef
                || callee is ExprAst.LocalRef
                || callee is ExprAst.MemberAccess
                || callee is ExprAst.SafeMemberAccess
                || callee is ExprAst.FunRefExpr
    }

    private fun parseCallArgs(): List<ExprAst> {
        p.expect(TokenType.LPAREN, "调用缺少 '('")
        val args = mutableListOf<ExprAst>()
        if (!p.check(TokenType.RPAREN)) {
            args += parse()
            while (p.check(TokenType.COMMA)) {
                p.advance()
                args += parse()
            }
        }
        p.expect(TokenType.RPAREN, "调用缺少 ')'")
        return args
    }

    /**
     * M5-b：解析调用实参列表，支持命名实参 `name = expr`。
     *
     * 返回 (位置实参, 命名实参)。命名实参后不允许再出现位置实参（E612）。
     * 同名命名实参重复报 E613。
     */
    private fun parseCallArgsWithNamed(): Pair<List<ExprAst>, List<top.wcpe.allincore.dsl.compiler.ast.NamedArg>> {
        p.expect(TokenType.LPAREN, "调用缺少 '('")
        val posArgs = mutableListOf<ExprAst>()
        val namedArgs = mutableListOf<top.wcpe.allincore.dsl.compiler.ast.NamedArg>()
        val seenNames = mutableSetOf<String>()
        var inNamedPhase = false

        if (!p.check(TokenType.RPAREN)) {
            parseOneCallArg(posArgs, namedArgs, seenNames, inNamedPhase).also { inNamedPhase = it }
            while (p.check(TokenType.COMMA)) {
                p.advance()
                p.skipNewlines()
                parseOneCallArg(posArgs, namedArgs, seenNames, inNamedPhase).also { inNamedPhase = it }
            }
        }
        p.expect(TokenType.RPAREN, "调用缺少 ')'")
        return Pair(posArgs, namedArgs)
    }

    /** 解析单个调用实参：判断是命名还是位置。返回是否进入命名阶段。 */
    private fun parseOneCallArg(
        posArgs: MutableList<ExprAst>,
        namedArgs: MutableList<top.wcpe.allincore.dsl.compiler.ast.NamedArg>,
        seenNames: MutableSet<String>,
        inNamedPhase: Boolean,
    ): Boolean {
        // 前瞻：IDENTIFIER 后跟 EQUALS（且不是 ==）→ 命名实参
        if (p.check(TokenType.IDENTIFIER) && p.peek(1).type == TokenType.EQUALS) {
            val nameTok = p.advance()
            p.advance() // 消费 '='
            p.skipNewlines()
            val value = parse()
            // E613：同名重复
            if (!seenNames.add(nameTok.lexeme)) {
                p.fail(top.wcpe.allincore.dsl.compiler.diag.DslError.E613_DUPLICATE_NAMED_ARG, nameTok.position, nameTok.lexeme)
            }
            namedArgs += top.wcpe.allincore.dsl.compiler.ast.NamedArg(nameTok.lexeme, value, nameTok.position)
            return true
        }
        // 位置实参
        if (inNamedPhase) {
            // E612：命名实参后不允许位置实参
            p.fail(top.wcpe.allincore.dsl.compiler.diag.DslError.E612_NAMED_ARG_BEFORE_POSITIONAL, p.current().position)
        }
        posArgs += parse()
        return inNamedPhase
    }

    // ---------- primary ----------
    private fun parsePrimary(): ExprAst {
        val t = p.current()
        return when (t.type) {
            TokenType.LPAREN -> {
                p.advance()
                val inner = parse()
                p.expect(TokenType.RPAREN, "括号表达式缺少 ')'")
                inner
            }
            TokenType.STRING -> {
                p.advance()
                ExprAst.Literal.StringLit(t.literalValue as String, t.position)
            }
            TokenType.NUMBER -> {
                p.advance()
                ExprAst.Literal.NumberLit(t.literalValue as Number, t.position)
            }
            TokenType.BOOLEAN -> {
                p.advance()
                ExprAst.Literal.BoolLit(t.literalValue as Boolean, t.position)
            }
            TokenType.NULL -> {
                p.advance()
                ExprAst.NullLit(t.position)
            }
            TokenType.KW_STATE -> parseStateRef()
            TokenType.KW_SESSION -> parseSessionRef()
            TokenType.KW_PERSIST -> parsePersistRef()
            TokenType.KW_DERIVED -> parseDerivedRef()
            TokenType.KW_PROPS -> parsePropsRef()
            TokenType.KW_THEME -> parseThemeRef()
            TokenType.KW_NUMBER, TokenType.KW_BOOL -> parseTypeKeywordBuiltin()
            TokenType.IDENTIFIER -> {
                // M5-e：识别 `suspend { ... }` 显式 suspend lambda（suspend 为软关键字）。
                // 前瞻：当前为 IDENTIFIER "suspend"，且其后（跳过 NEWLINE）是 LBRACE。
                if (t.lexeme == SOFT_KW_SUSPEND && lambdaBodyParser != null && nextNonNewlineIsLBrace()) {
                    p.advance() // 消费 suspend
                    p.skipNewlines()
                    return parseLambda(explicitSuspend = true)
                }
                // C5：识别 `net.request(...)` 专用形态。
                // net/request 都不是关键字，要求 lexeme 精确匹配。
                if (t.lexeme == "net"
                    && p.peek(1).type == TokenType.DOT
                    && p.peek(2).type == TokenType.IDENTIFIER
                    && p.peek(2).lexeme == "request"
                    && p.peek(3).type == TokenType.LPAREN
                ) {
                    // Stage 5：无论 await 与否，都转 MemberCall，由 NativeClassResolverPass lower 回 NetRequestCall。
                    val netReq = parseNetRequestCall()
                    netRequestToMemberCall(netReq)
                } else if (t.lexeme == "net"
                    && p.peek(1).type == TokenType.DOT
                    && p.peek(2).type == TokenType.IDENTIFIER
                    && p.peek(2).lexeme == "subscribe"
                    && p.peek(3).type == TokenType.LPAREN
                ) {
                    // Stage 5：非 await 上下文产出 MemberCall，由 NativeClassResolverPass lower 回 NetSubscribeCall。
                    val netSub = parseNetSubscribeCall()
                    netSubscribeToMemberCall(netSub)
                } else {
                    parseIdentifierStart()
                }
            }
            TokenType.INTERPOLATED_STRING_START -> parseInterp()
            TokenType.LBRACKET -> parseListLiteral()
            TokenType.LBRACE -> parseLambda()
            TokenType.HASH_LBRACE -> parseMapLiteral()
            TokenType.DOUBLE_COLON -> parseFunRef()
            TokenType.KW_AWAIT -> parseAwait()
            TokenType.KW_IF -> parseIfExpr()
            TokenType.KW_WHEN -> {
                val maker = whenParser
                    ?: p.fail("当前上下文不支持 when 表达式", t.position)
                maker()
            }
            else -> p.fail("不期望的表达式 token: '${t.lexeme}' (${t.type})")
        }
    }

    /** 前瞻：跳过 NEWLINE 后下一个 token 是否为 LBRACE（用于识别 `suspend { ... }`）。 */
    private fun nextNonNewlineIsLBrace(): Boolean {
        var k = 1
        while (p.peek(k).type == TokenType.NEWLINE) k++
        return p.peek(k).type == TokenType.LBRACE
    }

    /** 解析 `[a, b, c]` 列表字面量；元素之间允许换行。 */
    private fun parseListLiteral(): ExprAst.ListLiteralExpr {
        val lb = p.expect(TokenType.LBRACKET)
        p.skipNewlines()
        val items = mutableListOf<ExprAst>()
        if (!p.check(TokenType.RBRACKET)) {
            items += parse()
            p.skipNewlines()
            while (p.check(TokenType.COMMA)) {
                p.advance()
                p.skipNewlines()
                if (p.check(TokenType.RBRACKET)) break // 允许尾随逗号
                items += parse()
                p.skipNewlines()
            }
        }
        p.expect(TokenType.RBRACKET, "列表字面量缺少 ']'")
        return ExprAst.ListLiteralExpr(items, p.completePosition(lb.position))
    }

    /**
     * M6-a：解析 `#{"k1": v1, "k2": v2}` Map 字面量。
     *
     * - 空 `#{}` 合法
     * - 键必须是 STRING 字面量；其它形态报 E736
     * - 条目之间用 `,` 分隔，允许换行与尾随逗号
     */
    private fun parseMapLiteral(): ExprAst.MapLiteralExpr {
        val open = p.expect(TokenType.HASH_LBRACE, "Map 字面量缺少 '#{'")
        p.skipNewlines()
        val entries = mutableListOf<ExprAst.MapEntry>()
        if (!p.check(TokenType.RBRACE)) {
            entries += parseMapEntry()
            p.skipNewlines()
            while (p.check(TokenType.COMMA)) {
                p.advance()
                p.skipNewlines()
                if (p.check(TokenType.RBRACE)) break // 允许尾随逗号
                entries += parseMapEntry()
                p.skipNewlines()
            }
        }
        p.expect(TokenType.RBRACE, "Map 字面量缺少 '}'")
        return ExprAst.MapLiteralExpr(entries, p.completePosition(open.position))
    }

    /** 解析单个 Map 字面量条目 `"key": expr`；键非字符串字面量报 E736。 */
    private fun parseMapEntry(): ExprAst.MapEntry {
        val keyTok = p.current()
        if (keyTok.type != TokenType.STRING) {
            p.fail(DslError.E758_MAP_KEY_NOT_STRING_LITERAL, keyTok.position)
        }
        p.advance()
        p.expect(TokenType.COLON, "Map 字面量键后缺少 ':'")
        p.skipNewlines()
        val value = parse()
        return ExprAst.MapEntry(
            key = keyTok.literalValue as String,
            value = value,
            position = keyTok.position,
        )
    }

    /** 解析类型关键字形式的转换内建调用：Number(value) / Boolean(value)。 */
    private fun parseTypeKeywordBuiltin(): ExprAst.BuiltinCallExpr {
        val keyword = p.current()
        val name = when (keyword.type) {
            TokenType.KW_NUMBER -> "Number"
            TokenType.KW_BOOL -> "Boolean"
            else -> p.fail("不支持的转换函数: ${keyword.lexeme}", keyword.position)
        }
        p.advance()
        if (!p.check(TokenType.LPAREN)) {
            p.fail("$name 后缺少 '('，正确语法为 $name(value)", keyword.position)
        }
        val (posArgs, namedArgs) = parseCallArgsWithNamed()
        val trailing = tryParseTrailingLambda()
        if (trailing != null) {
            p.fail("内建 $name() 不支持 trailing lambda", keyword.position)
        }
        return ExprAst.BuiltinCallExpr(name, posArgs, p.completePosition(keyword.position), namedArgs)
    }

    private fun parseStateRef(): ExprAst.StateRef {
        val kw = p.expect(TokenType.KW_STATE)
        p.expect(TokenType.DOT, "state 后缺少 '.'")
        val name = p.expect(TokenType.IDENTIFIER, "state 后缺少字段名")
        p.rejectReservedUserIdentifier(name.lexeme, name.position)
        return ExprAst.StateRef(name.lexeme, kw.position.endingAt(name.range.end))
    }

    /**
     * 解析 `session.<name>` 表达式引用。
     *
     * <p>仅作为读取形态出现；session.x = ... 赋值由 [ActionBlockParser] 在语句层处理。
     * 与 state.x 不同的是，session 字段不要求在 session defaults 块中预先声明：
     * 因为 session state 跨 screen / hud 共享，A screen 写入的字段会被 B 屏幕读到，
     * 编译期单文件诊断不足以覆盖。runtime 在未声明字段读取时返回 null（与 StateRef 一致）。</p>
     *
     * <p>方法调用形态 `session.method(args)` 改派为 MemberCall(IdentRef("session"), method, args)，
     * 让 runtime CallDispatcher 路由到 SessionApi。</p>
     */
    private fun parseSessionRef(): ExprAst {
        val kw = p.expect(TokenType.KW_SESSION)
        p.expect(TokenType.DOT, "session 后缺少 '.'")
        val name = p.expect(TokenType.IDENTIFIER, "session 后缺少字段名")
        p.rejectReservedUserIdentifier(name.lexeme, name.position)
        if (p.check(TokenType.LPAREN)) {
            // 方法调用：发到 SessionApi（runtime 端 IdentRef("session") → SessionApi.INSTANCE）
            val (posArgs, namedArgs) = parseCallArgsWithNamed()
            val target = ExprAst.IdentRef("session", kw.position)
            return ExprAst.MemberCall(target, name.lexeme, posArgs, p.completePosition(kw.position), namedArgs)
        }
        val field = parseOptionalNamespaceField(name.lexeme)
        return ExprAst.SessionRef(field, p.completePosition(kw.position))
    }

    /** 解析 `persist.<name>` 表达式引用。语义见 [parseSessionRef]，仅 scope 不同。 */
    private fun parsePersistRef(): ExprAst {
        val kw = p.expect(TokenType.KW_PERSIST)
        p.expect(TokenType.DOT, "persist 后缺少 '.'")
        val name = p.expect(TokenType.IDENTIFIER, "persist 后缺少字段名")
        p.rejectReservedUserIdentifier(name.lexeme, name.position)
        if (p.check(TokenType.LPAREN)) {
            val (posArgs, namedArgs) = parseCallArgsWithNamed()
            val target = ExprAst.IdentRef("persist", kw.position)
            return ExprAst.MemberCall(target, name.lexeme, posArgs, p.completePosition(kw.position), namedArgs)
        }
        val field = parseOptionalNamespaceField(name.lexeme)
        return ExprAst.PersistRef(field, p.completePosition(kw.position))
    }

    /** 解析 v2 `scope.namespace.field`，运行期仍使用扁平 `namespace.field` key。 */
    private fun parseOptionalNamespaceField(first: String): String {
        if (!p.isStrictMode() || !p.check(TokenType.DOT) || p.peek(1).type != TokenType.IDENTIFIER) {
            return first
        }
        p.advance()
        val field = p.expect(TokenType.IDENTIFIER, "命名空间后缺少字段名")
        p.rejectReservedUserIdentifier(field.lexeme, field.position)
        return "$first.${field.lexeme}"
    }

    /**
     * 解析 `derived.<name>` 派生值引用。
     *
     * <p>仅作为只读读取形态出现；derived 不可写（`derived.x = ...` 在语句层被拒绝），
     * 也没有方法调用形态。derived 字段必须在 `derived defaults { ... }` 块中预先声明，
     * 引用合法性由语义层 [top.wcpe.allincore.dsl.compiler.semantic.ReferenceResolver] 校验。</p>
     */
    private fun parseDerivedRef(): ExprAst {
        val kw = p.expect(TokenType.KW_DERIVED)
        p.expect(TokenType.DOT, "derived 后缺少 '.'")
        val name = p.expect(TokenType.IDENTIFIER, "derived 后缺少字段名")
        p.rejectReservedUserIdentifier(name.lexeme, name.position)
        return ExprAst.DerivedRef(name.lexeme, kw.position.endingAt(name.range.end))
    }

    private fun parsePropsRef(): ExprAst.PropsRef {
        val kw = p.expect(TokenType.KW_PROPS)
        p.expect(TokenType.DOT, "props 后缺少 '.'")
        val name = expectMemberName("props 后缺少字段名")
        return ExprAst.PropsRef(name.lexeme, kw.position.endingAt(name.range.end))
    }

    /**
     * 标识符起始的解析顺序：
     *
     * 1. `name(args)` / `name(args){lambda}` / `name { lambda }` → [ExprAst.CallExpr]
     *    （callee 按当前作用域升格：局部 var → [ExprAst.LocalRef]；
     *    screen 级 fun → [ExprAst.FunRefExpr]；其它 → [ExprAst.IdentRef]）
     * 2. 已声明的局部 `var`            → [ExprAst.LocalRef]
     * 3. [allowBareIdent] 或后跟 `.`/`?.` → [ExprAst.IdentRef]
     * 4. 否则报错
     */
    private fun parseIdentifierStart(): ExprAst {
        val ident = p.expect(TokenType.IDENTIFIER)
        if (p.check(TokenType.LPAREN)) {
            // M5-b：与 parsePostfix 通用调用路径一致，支持命名实参
            val (posArgs, namedArgs) = parseCallArgsWithNamed()
            val tl = tryParseTrailingLambda()
            // C6 + RFC §3.3：内建函数白名单（未被局部 var / screen 级 fun 遮蔽时生效）
            if (ident.lexeme in BUILTIN_FUNCTION_NAMES
                && !p.isLocal(ident.lexeme) && !p.isScreenFun(ident.lexeme)
            ) {
                if (tl != null) {
                    p.fail("内建 ${ident.lexeme}() 不支持 trailing lambda", ident.position)
                }
                return ExprAst.BuiltinCallExpr(ident.lexeme, posArgs, p.completePosition(ident.position), namedArgs)
            }
            // 统一走 CallExpr：callee 按作用域分类升格。
            val callee: ExprAst = when {
                p.isLocal(ident.lexeme) -> ExprAst.LocalRef(ident.lexeme, ident.position)
                p.isScreenFun(ident.lexeme) -> ExprAst.FunRefExpr(ident.lexeme, ident.position)
                else -> ExprAst.IdentRef(ident.lexeme, ident.position)
            }
            val finalArgs = if (tl != null) posArgs + tl else posArgs
            return ExprAst.CallExpr(callee, finalArgs, p.completePosition(ident.position), namedArgs)
        }
        if (p.isLocal(ident.lexeme)) {
            return ExprAst.LocalRef(ident.lexeme, ident.position)
        }
        // Phase B.4：source 形参（`column "x" { item -> ... }` 的 `item`）允许裸引用。
        // 与 var 类局部隔离：升格为 IdentRef，运行时由 IrInterpreter 写入 EvalContext.locals 并
        // 由 ExprEvaluator 在 IdentRef 分支以 ctx.getLocals().get() 取出。
        if (p.isSourceParam(ident.lexeme)) {
            return ExprAst.IdentRef(ident.lexeme, ident.position)
        }
        if (allowBareIdent || p.check(TokenType.DOT) || p.check(TokenType.SAFE_CALL) || p.check(TokenType.LBRACE)) {
            return ExprAst.IdentRef(ident.lexeme, ident.position)
        }
        p.fail("标识符 '${ident.lexeme}' 后缺少 '('", ident.position)
    }

    /**
     * 解析 `await net.request(...)` 及其后缀链表达式（D1）。
     *
     * 形态：
     * - `await net.request(...)` —— 裸 await，AwaitExpr.tailExpr 为 null
     * - `await net.request(...).a.b("k")[i]` —— AwaitExpr.tailExpr 为整条后缀链，
     *   其中根 NetRequestCall 已被替换为 LocalRef("__result") 占位
     */
    private fun parseAwait(): ExprAst.AwaitExpr {
        val awaitTok = p.expect(TokenType.KW_AWAIT, "缺少 'await' 关键字")
        p.enterAwait()
        val inner = try {
            parseUnary()
        } finally {
            p.exitAwait()
        }
        // 1) 优先识别 await net.request(...) 形态（含后缀链）
        val root = findNetRequestRoot(inner)
        if (root != null) {
            // Stage 5：E603 检查——await 不允许携带 callback
            if (root is ExprAst.NetRequestCall && root.callback != null) {
                p.fail(DslError.E603_AWAIT_HAS_CALLBACK, awaitTok.position)
            }
            if (root is ExprAst.MemberCall && root.method == "request" && root.args.size == 4) {
                // 4 args 意味着有 callback（第 4 个参数）
                p.fail(DslError.E603_AWAIT_HAS_CALLBACK, awaitTok.position)
            }
            val tailExpr = if (inner === root) null else buildTailExpr(inner, root)
            return ExprAst.AwaitExpr(
                netRequestCall = root,
                position = awaitTok.position.endingAt(inner.range.end),
                tailExpr = tailExpr,
            )
        }
        // 2) M4 A2 Stage 1：await f(args) 形态（suspend lambda 调用，无 tailExpr）
        // 任务 3 通用 await：放宽到 MemberCall（如 await timer.delayAsync(1000)）。
        // runtime 在末位求值结果若实现 PendingAwaitable 时挂起等待，否则按同步返回值立即 resume。
        if (inner is ExprAst.CallExpr || inner is ExprAst.MemberCall) {
            return ExprAst.AwaitExpr(
                netRequestCall = null,
                position = awaitTok.position.endingAt(inner.range.end),
                tailExpr = null,
                suspendCall = inner,
            )
        }
        // 3) 都不匹配 → E606
        p.fail(
            DslError.E606_AWAIT_REQUIRES_SUSPEND_TARGET,
            awaitTok.position,
            inner::class.simpleName ?: "?",
        )
    }

    /**
     * 沿着 inner 的后缀访问链向 target 方向递归，找到根节点 NetRequestCall 或
     * MemberCall(IdentRef("net"), "request")。
     * 仅允许中间节点为 MemberAccess / SafeMemberAccess / MemberCall / SafeMemberCall / IndexExpr。
     */
    private fun findNetRequestRoot(e: ExprAst): ExprAst? = when (e) {
        is ExprAst.NetRequestCall -> e
        is ExprAst.MemberCall -> {
            val tgt = e.target
            if (tgt is ExprAst.IdentRef && tgt.name == "net" && e.method == "request") e
            else findNetRequestRoot(e.target)
        }
        is ExprAst.MemberAccess -> findNetRequestRoot(e.target)
        is ExprAst.SafeMemberAccess -> findNetRequestRoot(e.target)
        is ExprAst.SafeMemberCall -> findNetRequestRoot(e.target)
        is ExprAst.IndexExpr -> findNetRequestRoot(e.target)
        else -> null
    }

    /**
     * 重建 [original] 为整条 tailExpr：把根 [root] 替换为 IdentRef("__result")，
     * 保持外层 MemberAccess / MemberCall / IndexExpr 等链结构不变。
     *
     * 使用 IdentRef 而非 LocalRef：运行时 await 完成回调通过 `withLocals` 把合成的
     * result 写到 locals 帧；ExprEvaluator 的 IdentRef 分支正好从 locals 读取，
     * LocalRef 走 var 帧栈会找不到 `__result`。
     */
    private fun buildTailExpr(original: ExprAst, root: ExprAst): ExprAst {
        if (original === root) {
            return ExprAst.IdentRef("__result", root.position)
        }
        return when (original) {
            is ExprAst.MemberAccess -> original.copy(target = buildTailExpr(original.target, root))
            is ExprAst.SafeMemberAccess -> original.copy(target = buildTailExpr(original.target, root))
            is ExprAst.MemberCall -> original.copy(target = buildTailExpr(original.target, root))
            is ExprAst.SafeMemberCall -> original.copy(target = buildTailExpr(original.target, root))
            is ExprAst.IndexExpr -> original.copy(target = buildTailExpr(original.target, root))
            else -> error("buildTailExpr 不支持的链节点：${original::class.simpleName}")
        }
    }

    /**
     * 解析 if 表达式 `if (cond) thenExpr else elseExpr`。
     * <p>
     * 与 if 语句的区别：
     *  - 必须含 else 分支（保证表达式总有值）
     *  - 分支体仅允许单个表达式（不接受 `{ ... }` 语句块）；
     *    需要语句块或多分支条件，请改用 if 语句或 when 表达式。
     * </p>
     */
    private fun parseIfExpr(): ExprAst.IfExpr {
        val ifTok = p.expect(TokenType.KW_IF)
        p.expect(TokenType.LPAREN, "if 后缺少 '('")
        val cond = parse()
        p.expect(TokenType.RPAREN, "if 条件缺少 ')'")
        p.skipNewlines()
        val thenBody = parseIfExprBranch()
        p.skipNewlines()
        p.expect(TokenType.KW_ELSE, "if 表达式必须包含 else")
        p.skipNewlines()
        val elseBody = parseIfExprBranch()
        return ExprAst.IfExpr(cond, thenBody, elseBody, p.completePosition(ifTok.position))
    }

    private fun parseIfExprBranch(): List<StmtAst> {
        if (p.check(TokenType.LBRACE)) {
            p.fail("if 表达式分支暂只支持单个表达式；语句块请使用 if 语句", p.current().position)
        }
        val pos = p.current().position
        val parsed = parse()
        return listOf(StmtAst.ExprStmt(parsed, pos.endingAt(parsed.range.end)))
    }

    /**
     * 解析 `::name` 函数引用，支持 M6-e 部分应用形态 `::name(args)`。
     *
     * - 纯引用 `::name`：boundArgs/boundNamedArgs 均为空
     * - `::name()`：等价 `::name`（空括号视为无 bound 实参）
     * - `::name(a, b, k = v)`：实参在创建时 snapshot，调用时与运行期实参合并
     */
    private fun parseFunRef(): ExprAst.FunRefExpr {
        val tok = p.expect(TokenType.DOUBLE_COLON, "函数引用缺少 '::'")
        val name = p.expect(TokenType.IDENTIFIER, "'::' 后缺少函数名")
        if (!p.check(TokenType.LPAREN)) {
            return ExprAst.FunRefExpr(name.lexeme, tok.position.endingAt(name.range.end))
        }
        val (posArgs, namedArgs) = parseCallArgsWithNamed()
        return ExprAst.FunRefExpr(name.lexeme, p.completePosition(tok.position), posArgs, namedArgs)
    }

    /**
     * 解析 lambda 字面量：`{ x, y -> body }` 或 `{ body }`（隐式 `it`）。
     *
     * 探测策略：消费 `{` 后，向前扫描，若在遇到 `}` / `LBRACE` / EOF 之前
     * 遇到 `->`，则视为有显式参数；否则视为无参数（隐式 it）。参数列表仅
     * 接受逗号分隔的标识符。
     *
     * @param explicitSuspend 调用方已识别并消费过 `suspend` 软关键字前缀时传入 true，
     *        用户显式声明 `suspend { ... }` 时强制 lambda 视为 suspend；
     *        未显式声明则沿用 [LambdaSuspendDetector] 的自动推断（保持对旧代码的向后兼容）。
     */
    private fun parseLambda(explicitSuspend: Boolean = false): ExprAst.LambdaExpr {
        val maker = lambdaBodyParser
            ?: p.fail("当前上下文不支持 lambda 表达式", p.current().position)
        val lb = p.expect(TokenType.LBRACE, "lambda 缺少 '{'")
        p.skipNewlines()

        val params = mutableListOf<String>()
        // M5-b：lambda 形参也支持默认值 `{ x, y = 10 -> body }`
        val defaults = mutableListOf<ExprAst?>()
        // M5-c：lambda 形参也支持类型标注 `{ x: Int, y: String = "a" -> body }`
        val paramTypes = mutableListOf<top.wcpe.allincore.dsl.compiler.ast.TypeAst?>()
        if (hasArrowBeforeRBrace()) {
            // 形如 `{ x, y ->` / `{ x ->` / `{ -> ... }`（显式空参数）
            if (p.check(TokenType.ARROW)) {
                p.advance() // 直接消费 ARROW，参数列表为空
                p.skipNewlines()
            } else {
                parseLambdaParam(params, defaults, paramTypes, seenDefault = false).let {
                    var seen = it
                    while (p.check(TokenType.COMMA)) {
                        p.advance()
                        p.skipNewlines()
                        seen = parseLambdaParam(params, defaults, paramTypes, seen)
                    }
                }
                p.expect(TokenType.ARROW, "lambda 参数列表缺少 '->'")
                p.skipNewlines()
            }
        } else {
            // 隐式 it：单参数
            params += "it"
            defaults += null
            paramTypes += null
        }
        // I3：默认值不得引用后续形参
        top.wcpe.allincore.dsl.compiler.semantic.DefaultValueChecker.checkAndFail(p, params, defaults)
        val body = maker(params)
        p.expect(TokenType.RBRACE, "lambda 缺少 '}'")
        // M5-e：移除自动推断，仅在用户显式 `suspend { ... }` 时标记 isSuspend=true。
        // 未显式声明 suspend 的 lambda 含 await 将由 SuspendKeywordChecker 报 E625。
        val isSuspend = explicitSuspend
        return ExprAst.LambdaExpr(params, body, p.completePosition(lb.position), isSuspend, defaults, paramTypes)
    }

    /**
     * M5-b / M5-c：解析单个 lambda 形参（支持 `name`、`name: Type`、`name = default`、
     * `name: Type = default`）。必填形参不能出现在有默认值之后（E616）。
     */
    private fun parseLambdaParam(
        params: MutableList<String>,
        defaults: MutableList<ExprAst?>,
        paramTypes: MutableList<top.wcpe.allincore.dsl.compiler.ast.TypeAst?>,
        seenDefault: Boolean,
    ): Boolean {
        val nameTok = p.expect(TokenType.IDENTIFIER, "lambda 参数名缺失")
        params += nameTok.lexeme
        // M5-c：可选类型标注 `: Type`
        val paramType = if (p.check(TokenType.COLON)) {
            p.advance()
            TypeParser.parse(p)
        } else null
        paramTypes += paramType
        return if (p.check(TokenType.EQUALS)) {
            p.advance()
            p.skipNewlines()
            defaults += parse()
            true
        } else {
            if (seenDefault) {
                p.fail(
                    top.wcpe.allincore.dsl.compiler.diag.DslError.E616_REQUIRED_AFTER_DEFAULT,
                    nameTok.position,
                    nameTok.lexeme,
                )
            }
            defaults += null
            seenDefault
        }
    }

    /**
     * 向前 peek 探测：当前 `{` 已被消费，从当前位置起，未越过同级 `{`/`}`
     * 之前是否能遇到 `->`。仅当头部为合法形参列表 token 时才视为显式参数。
     *
     * 形参列表合法 token：
     * - IDENTIFIER：参数名 / 类型名
     * - COMMA / NEWLINE：参数分隔与换行
     * - COLON：类型标注 `name: Type`
     * - QUESTION：可空类型 `Type?`
     * - LT / GT / DOT：泛型 / 限定名 `Map<String, Foo.Bar>`
     *
     * **M10 Stage 4**：把 COLON / QUESTION / LT / GT / DOT 加入白名单，
     * 让 `{ e: Number -> ... }` 等类型标注 lambda 真正可用，避免 net.request callback
     * 标注类型时被误识别为隐式 it lambda 而 parser 报 E002。EQUALS（默认值）暂不
     * 加入白名单——默认值表达式可能含任意 token，简单白名单扫描难以覆盖。
     */
    /**
     * 向前 peek 探测：当前 `{` 已被消费，从当前位置起，未越过同级 `{`/`}`
     * 之前是否能遇到 `->`。仅当头部为合法形参列表 token 时才视为显式参数。
     *
     * 形参列表合法 token：
     * - IDENTIFIER：参数名 / 类型名
     * - COMMA / NEWLINE：参数分隔与换行
     * - COLON：类型标注 `name: Type`
     * - QUESTION：可空类型 `Type?`
     * - LT / GT / DOT：泛型 / 限定名 `Map<String, Foo.Bar>`
     * - KW_STRING / KW_NUMBER / KW_BOOL / KW_COLOR：内置类型关键字（lexer 把
     *   String / Number / Bool / Color 词法识别为这些独立 token，不是 IDENTIFIER）
     *
     * **M10 Stage 4**：把 COLON / QUESTION / LT / GT / DOT 与 KW_* 加入白名单，
     * 让 `{ e: Number -> ... }` 等类型标注 lambda 真正可用，避免 net.request callback
     * 标注类型时被误识别为隐式 it lambda 而 parser 报 E002。EQUALS（默认值）暂不
     * 加入白名单——默认值表达式可能含任意 token，简单白名单扫描难以覆盖。
     */
    private fun hasArrowBeforeRBrace(): Boolean {
        var k = 0
        while (true) {
            val t = p.peek(k).type
            when (t) {
                TokenType.ARROW -> return true
                TokenType.RBRACE, TokenType.EOF, TokenType.LBRACE -> return false
                TokenType.IDENTIFIER, TokenType.COMMA, TokenType.NEWLINE,
                TokenType.COLON, TokenType.QUESTION,
                TokenType.LT, TokenType.GT, TokenType.DOT,
                TokenType.KW_STRING, TokenType.KW_NUMBER, TokenType.KW_BOOL, TokenType.KW_COLOR,
                -> k++
                else -> return false
            }
        }
    }

    private fun parseThemeRef(): ExprAst.ThemeRef {
        val kw = p.expect(TokenType.KW_THEME)
        p.expect(TokenType.DOT, "theme 后缺少 '.'，正确语法为 theme.<key>")
        val parts = mutableListOf(p.expect(TokenType.IDENTIFIER, "theme. 后缺少 token 名").lexeme)
        while (p.check(TokenType.DOT)) {
            p.advance()
            parts += p.expect(TokenType.IDENTIFIER, "theme token 引用缺少字段名").lexeme
        }
        return ExprAst.ThemeRef(parts.joinToString("."), p.completePosition(kw.position))
    }

    private fun parseInterp(): ExprAst.Interp {
        val startTok = p.expect(TokenType.INTERPOLATED_STRING_START)
        val parts = mutableListOf<ExprAst.Interp.InterpPart>()

        val firstPart = p.expect(TokenType.STRING_PART, "插值字符串缺少首段文本")
        parts += ExprAst.Interp.InterpPart.TextPart(firstPart.literalValue as String)

        while (p.check(TokenType.INTERPOLATION_START)) {
            p.advance()
            val expr = parse()
            parts += ExprAst.Interp.InterpPart.ExprPart(expr)
            p.expect(TokenType.INTERPOLATION_END, "插值表达式缺少 '}'")
            val tail = p.expect(TokenType.STRING_PART, "插值表达式后缺少文本片段")
            parts += ExprAst.Interp.InterpPart.TextPart(tail.literalValue as String)
        }

        return ExprAst.Interp(parts, p.completePosition(startTok.position))
    }

    // ===== C5：net.request(...) 专用解析 =====

    /**
     * 解析 `net.request("ns.handler", { payload }, callback, timeoutMs = N)` 或
     * trailing lambda 形态 `net.request("ns.handler", { payload }) { result -> ... }`。
     *
     * 位置参数顺序：(handlerKey, payload, callback?)，命名参数 `timeoutMs = expr`
     * （单位：毫秒）只能位于位置参数之后；callback 也可通过 trailing lambda 提供，
     * 与位置 callback 互斥。
     */
    private fun parseNetRequestCall(): ExprAst.NetRequestCall {
        val netTok = p.expect(TokenType.IDENTIFIER) // net
        p.expect(TokenType.DOT, "net 后缺少 '.'")
        val requestTok = p.expect(TokenType.IDENTIFIER, "net. 后缺少 'request'")
        if (requestTok.lexeme != "request") {
            p.fail("net. 后缺少 'request'", requestTok.position)
        }
        p.expect(TokenType.LPAREN, "net.request 缺少 '('")
        p.skipNewlines()
        // 1) handlerKey 字符串字面量 "ns.handler"
        val key = parseNetHandlerKey()
        p.skipNewlines()
        p.expect(TokenType.COMMA, "net.request handlerKey 后缺少 ','")
        p.skipNewlines()
        // 2) payload Map 字面量 #{...}；M6-a 起强制要求 # 前缀，旧 `{ k = v }` 报 E730
        if (p.check(TokenType.LBRACE)) {
            p.fail(DslError.E747_MAP_LITERAL_REQUIRES_HASH_PREFIX, p.current().position)
        }
        if (!p.check(TokenType.HASH_LBRACE)) {
            p.fail(
                "net.request 第二个参数必须是 Map 字面量 #{\"k\": v}",
                p.current().position,
            )
        }
        val mapLit = parseMapLiteral()
        val payload = mapLit.entries.map { e ->
            ExprAst.NetPayloadEntry(key = e.key, value = e.value, position = e.position)
        }
        // 3) 可选位置 callback、命名参数 timeoutMs、trailing lambda
        val trailing = parseNetArgsAndTrailing(netTok.position)
        return ExprAst.NetRequestCall(
            namespace = key.namespace,
            handler = key.handler,
            payload = payload,
            callback = trailing.callback,
            timeoutMs = trailing.timeoutMs,
            position = p.completePosition(netTok.position),
        )
    }

    /**
     * Stage 4：把 parseNetRequestCall 解析得到的 NetRequestCall 转换为 MemberCall，
     * 以便 NativeClassResolverPass 通过描述符 lower 回 NetRequestCall。
     * 仅用于非 await 上下文，await 上下文继续直接使用 NetRequestCall。
     */
    private fun netRequestToMemberCall(call: ExprAst.NetRequestCall): ExprAst.MemberCall {
        val pos = call.position
        val targetIdent = ExprAst.IdentRef("net", pos)
        val payloadEntries = call.payload.map { e ->
            ExprAst.MapEntry(key = e.key, value = e.value, position = e.position)
        }
        val payloadMap = ExprAst.MapLiteralExpr(payloadEntries, pos)
        val args = if (call.callback != null) {
            listOf(
                ExprAst.Literal.StringLit(call.namespace, pos),
                ExprAst.Literal.StringLit(call.handler, pos),
                payloadMap,
                call.callback,
            )
        } else {
            listOf(
                ExprAst.Literal.StringLit(call.namespace, pos),
                ExprAst.Literal.StringLit(call.handler, pos),
                payloadMap,
            )
        }
        val namedArgs = if (call.timeoutMs != null) {
            listOf(NamedArg("timeoutMs", call.timeoutMs, pos))
        } else emptyList()
        return ExprAst.MemberCall(
            targetIdent,
            top.wcpe.allincore.dsl.compiler.semantic.`native`.HostClassNames.NetMethods.REQUEST,
            args,
            pos,
            namedArgs,
        )
    }

    /**
     * Stage 5：把 parseNetSubscribeCall 解析得到的 NetSubscribeCall 转换为 MemberCall，
     * 以便 NativeClassResolverPass 通过描述符 lower 回 NetSubscribeCall。
     */
    private fun netSubscribeToMemberCall(call: ExprAst.NetSubscribeCall): ExprAst.MemberCall {
        val pos = call.position
        val targetIdent = ExprAst.IdentRef("net", pos)
        val args = listOf(
            ExprAst.Literal.StringLit(call.namespace, pos),
            ExprAst.Literal.StringLit(call.topic, pos),
            call.handler,
        )
        return ExprAst.MemberCall(
            targetIdent,
            top.wcpe.allincore.dsl.compiler.semantic.`native`.HostClassNames.NetMethods.SUBSCRIBE,
            args,
            pos,
        )
    }

    /**
     * 解析 net.request 首个位置参数：支持两种形态
     * - 单字面量：`"namespace.handler"`，parser 拆 `.` 得到 (ns, handler)
     * - 双字面量：`"namespace", "handler"`，对不喜欢拼接的作者更友好
     *
     * 形态识别：第 1 个字符串字面量后，跳过 NEWLINE 看 `,` 之后的 token。
     * - 紧跟另一个 STRING → 双字面量；消费 `,` 与第二个 STRING，二者拼成 (ns, handler)
     * - 紧跟 HASH_LBRACE / 其它 → 单字面量；走原 `.` 拆分逻辑
     *
     * 命名合法性兜底：handler 含 `.` 一律拒绝，避免 `"ns", "a.b"` 之类绕过命名约束。
     */
    private fun parseNetHandlerKey(): NetHandlerKey {
        val firstTok = p.current()
        if (firstTok.type != TokenType.STRING) {
            p.fail("net.request 第一个参数必须是字符串字面量 'namespace.handler' 或 'namespace'", firstTok.position)
        }
        p.advance()
        // 双字面量预测：跳过 NEWLINE 看 `,` + STRING
        if (isTwoLiteralKeyForm()) {
            p.skipNewlines()
            p.advance() // 消费 ','
            p.skipNewlines()
            val secondTok = p.expect(TokenType.STRING, "net.request 第二个 key 字面量缺失")
            val ns = firstTok.literalValue as String
            val handler = secondTok.literalValue as String
            if (ns.isEmpty()) {
                p.fail("net.request namespace 不能为空字符串", firstTok.position)
            }
            if (handler.isEmpty()) {
                p.fail("net.request handler 不能为空字符串", secondTok.position)
            }
            if (handler.contains('.')) {
                p.fail("net.request handler 不允许包含 '.'，实际：'$handler'", secondTok.position)
            }
            return NetHandlerKey(ns, handler, firstTok.position)
        }
        // 单字面量：原 `"ns.handler"` 拆分逻辑
        val key = firstTok.literalValue as String
        val dotIdx = key.indexOf('.')
        if (dotIdx <= 0 || dotIdx >= key.length - 1) {
            p.fail("net.request handler 键必须形如 'namespace.handler'，实际：'$key'", firstTok.position)
        }
        val ns = key.substring(0, dotIdx)
        val handler = key.substring(dotIdx + 1)
        if (handler.contains('.')) {
            p.fail("net.request handler 键最多一个 '.'，实际：'$key'", firstTok.position)
        }
        return NetHandlerKey(ns, handler, firstTok.position)
    }

    /**
     * 双字面量形态前瞻：当前 token 是 `,`（或可跳过 NEWLINE 后到达 `,`）且 `,` 之后是 STRING 时返回 true。
     *
     * 用 peek 不消费 token，避免触发回溯——单字面量与双字面量的差异仅在第二个参数的类型上。
     */
    private fun isTwoLiteralKeyForm(): Boolean {
        var k = 0
        while (p.peek(k).type == TokenType.NEWLINE) k++
        if (p.peek(k).type != TokenType.COMMA) return false
        k++
        while (p.peek(k).type == TokenType.NEWLINE) k++
        return p.peek(k).type == TokenType.STRING
    }

    /**
     * 解析 payload 之后剩余的参数：位置 callback、命名参数 `timeoutMs = expr`、trailing lambda。
     * 互斥规则：位置 callback 与 trailing lambda 二选一；timeoutMs 不可重复，且不可早于位置 callback。
     * 进入时 token 指向 `)` 或 `,`；返回时已消耗到 trailing lambda 末尾（若有）。
     */
    private fun parseNetArgsAndTrailing(netPos: SourcePosition): NetTrailing {
        var callbackExpr: ExprAst? = null
        var timeoutMs: ExprAst? = null
        p.skipNewlines()
        while (p.check(TokenType.COMMA)) {
            p.advance(); p.skipNewlines()
            if (isTimeoutMsArg()) {
                timeoutMs = parseTimeoutMsArg(timeoutMs)
            } else {
                callbackExpr = parsePositionalCallback(callbackExpr, timeoutMs)
            }
            p.skipNewlines()
        }
        p.expect(TokenType.RPAREN, "net.request 调用缺少 ')'")
        // trailing lambda 形态
        if (p.check(TokenType.LBRACE)) {
            if (callbackExpr != null) {
                p.fail("net.request 已通过位置参数提供 callback，不允许 trailing lambda", p.current().position)
            }
            callbackExpr = parseLambda()
        }
        // C6：await net.request(...) 允许省略 callback；其它上下文仍然必填。
        if (callbackExpr == null && !p.inAwait()) {
            p.fail("net.request 必须提供 callback（位置参数或 trailing lambda）", netPos)
        }
        return NetTrailing(callbackExpr, timeoutMs)
    }

    /** 当前位置是否为命名参数 `timeoutMs = ...`。 */
    private fun isTimeoutMsArg(): Boolean =
        p.check(TokenType.IDENTIFIER)
            && p.current().lexeme == "timeoutMs"
            && p.peek(1).type == TokenType.EQUALS

    /** 解析 `timeoutMs = expr`；若已存在则报错。 */
    private fun parseTimeoutMsArg(prev: ExprAst?): ExprAst {
        val nameTok = p.advance()
        p.advance() // EQUALS
        if (prev != null) {
            p.fail("net.request 'timeoutMs' 命名参数重复", nameTok.position)
        }
        return parse()
    }

    /** 解析位置 callback；若已存在或在 timeoutMs 之后则报错。 */
    private fun parsePositionalCallback(prev: ExprAst?, timeoutMs: ExprAst?): ExprAst {
        if (prev != null) {
            p.fail("net.request 已提供 callback，不再接受额外位置参数", p.current().position)
        }
        if (timeoutMs != null) {
            p.fail("net.request 位置 callback 必须在 timeoutMs 之前", p.current().position)
        }
        return parseNetCallbackExpr()
    }
    /** 解析位置 callback 表达式，并校验其必须是 lambda 字面量或 `::fun` 引用。 */
    private fun parseNetCallbackExpr(): ExprAst {
        val expr = parse()
        if (expr !is ExprAst.LambdaExpr && expr !is ExprAst.FunRefExpr) {
            p.fail(
                "net.request callback 必须是 lambda 字面量或 '::fun' 引用",
                expr.position,
            )
        }
        return expr
    }
    /** net.request 第一个位置参数 `"ns.handler"` 拆分后的结果。 */
    private data class NetHandlerKey(
        val namespace: String,
        val handler: String,
        val position: SourcePosition,
    )

    /** net.request 尾部参数解析的结果：位置/trailing 来的 callback 及可选 timeoutMs。 */
    private data class NetTrailing(
        val callback: ExprAst?,
        val timeoutMs: ExprAst?,
    )

    // ===== Stage B：net.subscribe("ns.topic", handler) 专用解析 =====

    /**
     * 解析 `net.subscribe("namespace.topic", handler)` 内置调用。
     *
     * 与 [parseNetRequestCall] 完全对称的两参形态：
     * - 第 1 参 `"namespace.topic"` 字符串字面量，由 parser 拆为 `namespace` + `topic`，
     *   不在 IR 里保留一对零碎的 String 实参，使 net.subscribe 节点结构与 net.request 一致；
     * - 第 2 参 [handler] 是 lambda（1 形参）或 funRef，runtime 求值得到可调用对象后注册到
     *   SubscribeRouter。
     *
     * handler 形态校验、(ns, topic) 命名合法性等语义级检查由
     * [top.wcpe.allincore.dsl.compiler.semantic.SubscribeChecker] 负责，本 parser 仅
     * 做语法解析与"必须是字符串字面量"这种结构性兜底。
     */
    private fun parseNetSubscribeCall(): ExprAst.NetSubscribeCall {
        val netTok = p.expect(TokenType.IDENTIFIER) // net
        p.expect(TokenType.DOT, "net 后缺少 '.'")
        val subscribeTok = p.expect(TokenType.IDENTIFIER, "net. 后缺少 'subscribe'")
        if (subscribeTok.lexeme != "subscribe") {
            p.fail("net. 后缺少 'subscribe'", subscribeTok.position)
        }
        p.expect(TokenType.LPAREN, "net.subscribe 缺少 '('")
        p.skipNewlines()
        // 1) topicKey 字符串字面量 "namespace.topic"
        val key = parseNetTopicKey()
        p.skipNewlines()
        p.expect(TokenType.COMMA, "net.subscribe topicKey 后缺少 ','")
        p.skipNewlines()
        // 2) handler 表达式（lambda / funRef，由语义层校验形态）
        val handler = parse()
        p.skipNewlines()
        p.expect(TokenType.RPAREN, "net.subscribe 调用缺少 ')'")
        return ExprAst.NetSubscribeCall(
            namespace = key.namespace,
            topic = key.topic,
            handler = handler,
            position = p.completePosition(netTok.position),
        )
    }

    /**
     * 解析 net.subscribe 首个位置参数：支持两种形态
     * - 单字面量：`"namespace.topic"`，parser 拆 `.` 得到 (ns, topic)
     * - 双字面量：`"namespace", "topic"`，对不喜欢拼接的作者更友好
     *
     * 形态识别与 [parseNetHandlerKey] 完全对称：第 1 个 STRING 后看 `,` 之后是 STRING 还是 handler 表达式。
     * 区别仅在于"第二个分隔位置"：net.subscribe 的"剩余实参"不是 payload Map，而是 handler
     * （lambda / funRef），第 2 字面量后面的 `,` 之后才是 handler。
     */
    private fun parseNetTopicKey(): NetTopicKey {
        val firstTok = p.current()
        if (firstTok.type != TokenType.STRING) {
            p.fail("net.subscribe 第一个参数必须是字符串字面量 'namespace.topic' 或 'namespace'", firstTok.position)
        }
        p.advance()
        if (isTwoLiteralKeyForm()) {
            p.skipNewlines()
            p.advance() // 消费 ','
            p.skipNewlines()
            val secondTok = p.expect(TokenType.STRING, "net.subscribe 第二个 key 字面量缺失")
            val ns = firstTok.literalValue as String
            val topic = secondTok.literalValue as String
            if (ns.isEmpty()) {
                p.fail("net.subscribe namespace 不能为空字符串", firstTok.position)
            }
            if (topic.isEmpty()) {
                p.fail("net.subscribe topic 不能为空字符串", secondTok.position)
            }
            if (topic.contains('.')) {
                p.fail("net.subscribe topic 不允许包含 '.'，实际：'$topic'", secondTok.position)
            }
            return NetTopicKey(ns, topic, firstTok.position)
        }
        val key = firstTok.literalValue as String
        val dotIdx = key.indexOf('.')
        if (dotIdx <= 0 || dotIdx >= key.length - 1) {
            p.fail("net.subscribe topic 键必须形如 'namespace.topic'，实际：'$key'", firstTok.position)
        }
        val ns = key.substring(0, dotIdx)
        val topic = key.substring(dotIdx + 1)
        if (topic.contains('.')) {
            p.fail("net.subscribe topic 键最多一个 '.'，实际：'$key'", firstTok.position)
        }
        return NetTopicKey(ns, topic, firstTok.position)
    }

    /** [parseNetTopicKey] 返回的解析结果。 */
    private data class NetTopicKey(
        val namespace: String,
        val topic: String,
        val position: SourcePosition,
    )

    companion object {
        /** `suspend` 是软关键字（词法层为 IDENTIFIER），与 TypeParser / ScreenParser 保持一致。 */
        private const val SOFT_KW_SUSPEND = "suspend"

        /** 在成员访问位置可作为合法字段名的类型关键字集合 */
        private val typeKeywordTypes = setOf(
            TokenType.KW_STRING,
            TokenType.KW_NUMBER,
            TokenType.KW_BOOL,
            TokenType.KW_COLOR,
            TokenType.KW_FUN,
        )

        /**
         * RFC §3.3 内建函数白名单（共用 [top.wcpe.allincore.dsl.compiler.semantic.BuiltinFunctions.NAMES] 定义）。
         *
         * 未被局部 `var` / screen 级 `fun` 遮蔽时，这些名字的 `name(args)` 调用会被
         * 解析为 [ExprAst.BuiltinCallExpr]，由运行期 CallDispatcher 直接执行。
         */
        internal val BUILTIN_FUNCTION_NAMES: Set<String> =
            top.wcpe.allincore.dsl.compiler.semantic.BuiltinFunctions.NAMES
    }

    /**
     * 读取成员名 token：IDENTIFIER 或类型关键字（string/number/bool/color/fun）均合法。
     * 类型关键字出现在成员访问位置时语义上是普通标识符。
     */
    private fun expectMemberName(hint: String): Token {
        val cur = p.current()
        if (cur.type == TokenType.IDENTIFIER || cur.type in typeKeywordTypes) {
            return p.advance()
        }
        p.fail("$hint：期望 IDENTIFIER，实际为 ${cur.type}（'${cur.lexeme}'）", cur.position)
    }
}
